/**
 * Plan choice — "the customer picks ONE of a few monthly plans" (Proposals
 * V2 R11, docs/proposal-v2/S11.md §3.1). Shared client/server.
 *
 * A plan choice is a set of monthly lines that share a `choiceGroupId`
 * (`PlaygroundQuoteItem`, frozen on the snapshot's render items). Every
 * member is recurring and `isOptional: true`, and exactly one carries
 * `optionalDefault: true` — the recommended plan, the one the customer
 * starts on. The customer's pick rides the optional-line selection: the
 * picked member is accepted, every other member declined.
 *
 * Office totals, the rail, the frozen snapshot, the deal figures and the
 * emails count the DEFAULT member only (`countsTowardMonthly`); the
 * customer's live total follows their pick.
 *
 * Pure and isomorphic: no React, no Firebase, no Node, no app imports.
 */

/** A choice needs two plans to choose between. */
export const MIN_CHOICE_MEMBERS = 2;

/** At most four plans in one choice. */
export const MAX_CHOICE_MEMBERS = 4;

/** The fields the choice rules read (live quote lines and frozen render items alike). */
export interface ChoiceMember {
  /** The line's id — when both carry one, `countsTowardMonthly` matches lines by it. */
  id?: string;
  choiceGroupId?: string;
  optionalDefault?: boolean;
}

/** The group a line belongs to — a non-empty string id, else none. */
function groupIdOf(item: ChoiceMember): string | undefined {
  const id = item.choiceGroupId;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

/** Every plan choice among `items`: group id → its members, both in item order. */
export function choiceGroups<T extends ChoiceMember>(items: Iterable<T>): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const id = groupIdOf(item);
    if (id === undefined) continue;
    const members = groups.get(id);
    if (members) members.push(item);
    else groups.set(id, [item]);
  }
  return groups;
}

/**
 * The member a choice starts on (the recommended plan): the first member
 * whose `optionalDefault` is not `false`, else undefined.
 */
export function choiceDefault<T extends ChoiceMember>(members: readonly T[]): T | undefined {
  return members.find((m) => m.optionalDefault !== false);
}

/**
 * Does this line count toward the office's monthly figure? Yes when it is in
 * no plan choice, or when it is its choice's default (among `items`). The
 * default is matched by line `id` when both carry one (copies of a line
 * match), else by identity.
 */
export function countsTowardMonthly<T extends ChoiceMember>(item: T, items: Iterable<T>): boolean {
  const id = groupIdOf(item);
  if (id === undefined) return true;
  const kept = choiceDefault(choiceGroups(items).get(id) ?? []);
  if (kept === undefined) return false;
  return kept === item || (typeof item.id === 'string' && item.id.length > 0 && kept.id === item.id);
}
